import {
  DEFAULT_LOCALE,
  isLocale,
  NON_DEFAULT_LOCALES,
  type Locale,
} from "./i18n";

const BASE = import.meta.env.BASE_URL;

export type Route = "home" | "thankYou" | "notFound" | `article/${string}`;

export const NAVIGATE_EVENT = "tl:navigate";

function stripTrailing(p: string): string {
  return p.length > 1 ? p.replace(/\/+$/, "") : p;
}

export function articleSlugFromRoute(route: Route): string | null {
  return route.startsWith("article/") ? route.slice("article/".length) : null;
}

export function articleRoute(slug: string): Route {
  return `article/${slug}`;
}

// Costruisce il path (BASE-aware) per una route in una data lingua.
// L'italiano (default) non ha prefisso; le altre lingue hanno "/<locale>".
export function pathForRoute(route: Route, locale: Locale): string {
  const parts: string[] = [];
  if (locale !== DEFAULT_LOCALE) parts.push(locale);
  if (route === "thankYou") {
    parts.push("grazie");
  } else {
    const slug = articleSlugFromRoute(route);
    if (slug) {
      parts.push("articoli", slug);
    }
  }
  const rel = parts.join("/");
  return rel ? `${BASE}${rel}` : BASE;
}

// Rimuove BASE e l'eventuale prefisso lingua, restituendo il path "relativo"
// normalizzato (senza slash finale) usato per derivare la route.
function relativeWithoutLocale(pathname: string): string {
  const base = stripTrailing(BASE);
  let rel = pathname;
  if (base !== "/" && rel.startsWith(base)) {
    rel = rel.slice(base.length);
  }
  rel = stripTrailing(rel).replace(/^\//, "");
  const segs = rel ? rel.split("/") : [];
  if (segs.length && isLocale(segs[0]) && segs[0] !== DEFAULT_LOCALE) {
    segs.shift();
  }
  return `/${segs.join("/")}`;
}

export function localeFromPath(pathname: string): Locale {
  const base = stripTrailing(BASE);
  let rel = pathname;
  if (base !== "/" && rel.startsWith(base)) {
    rel = rel.slice(base.length);
  }
  rel = rel.replace(/^\//, "");
  const first = rel.split("/")[0];
  return first && isLocale(first) && first !== DEFAULT_LOCALE
    ? first
    : DEFAULT_LOCALE;
}

export function routeFromPath(pathname: string): Route {
  const rel = relativeWithoutLocale(pathname);
  if (rel === "/") return "home";
  if (rel === "/grazie") return "thankYou";
  const m = rel.match(/^\/articoli\/([^/]+)$/);
  if (m) return articleRoute(decodeURIComponent(m[1]));
  return "notFound";
}

// Naviga (SPA) verso una route. Se la lingua non è specificata, mantiene
// quella corrente (derivata dall'URL).
export function navigate(route: Route, locale?: Locale): void {
  if (typeof window === "undefined") return;
  const loc = locale ?? localeFromPath(window.location.pathname);
  if (loc !== localeFromPath(window.location.pathname)) {
    // A real document navigation loads only the new language, keeping links crawlable.
    window.location.assign(pathForRoute(route, loc));
    return;
  }
  if (route.startsWith("article/")) {
    void import("../data/articles").then(({ loadArticles }) => loadArticles(loc))
      .then(() => commitNavigation(route, loc))
      .catch(() => window.location.assign(pathForRoute(route, loc)));
    return;
  }
  commitNavigation(route, loc);
}

function commitNavigation(route: Route, loc: Locale): void {
  window.history.pushState({}, "", pathForRoute(route, loc));
  window.dispatchEvent(new Event(NAVIGATE_EVENT));
  window.scrollTo(0, 0);
}

export { NON_DEFAULT_LOCALES };
